import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
const API_BASE_URL = process.env.REACT_APP_API_URL;

export const createJob = createAsyncThunk(
  'jobs/createJob',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}dashboard/create-job`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating job:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteJob = createAsyncThunk(
  'jobs/deleteJob',
  async (jobId, { rejectWithValue }) => {
    try {
      await axios.delete(
        `${API_BASE_URL}dashboard/delete-job/${jobId}`
      );
      return jobId;
    } catch (error) {
      console.error('Error deleting job:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateJob = createAsyncThunk(
  'jobs/updateJob',
  async ({ jobId, formData }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}dashboard/update-job/${jobId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating job:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchJobs = createAsyncThunk(
  'jobs/fetchJobs',
  async ({ page, limit, search, job_status, deleted }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        title: search || '',
        job_status: job_status || '',
        ...(deleted !== undefined && { deleted: deleted.toString() }) // Add deleted param only if defined
      });
      const response = await axios.get(
        `${API_BASE_URL}dashboard/get-all-dashboard-jobs?${params.toString()}`
      );
      return {
        items: response.data.items,
        total: response.data.totalItems,
        currentPage: response.data.page,
        pageSize: limit,
        totalPages: Math.ceil(response.data.totalItems / limit),
        itemCount: response.data.items.length
      };
    } catch (error) {
      console.error('Error fetching jobs:', error.response?.data?.message || error.message);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const toggleJobStatus = createAsyncThunk(
  'jobs/toggleJobStatus',
  async (jobId, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}dashboard/toggle-job-status/${jobId}`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error toggling job status:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const jobSlice = createSlice({
  name: 'jobs',
  initialState: {
    jobs: [],
    total: 0,
    loading: false,
    error: null,
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    itemCount: 0,
    success: false
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createJob.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createJob.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.jobs.unshift(action.payload);
        state.total += 1;
        state.totalPages = Math.ceil(state.total / state.pageSize);
      })
      .addCase(createJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateJob.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateJob.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        const index = state.jobs.findIndex(p => p.id === action.payload.jobId);
        if (index !== -1) {
          state.jobs[index] = { ...state.jobs[index], ...action.payload };
        }
      })
      .addCase(updateJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchJobs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobs.fulfilled, (state, action) => {
        state.loading = false;
        state.jobs = action.payload.items;
        state.total = action.payload.total;
        state.currentPage = action.payload.currentPage;
        state.pageSize = action.payload.pageSize;
        state.totalPages = action.payload.totalPages;
        state.itemCount = action.payload.itemCount;
      })
      .addCase(fetchJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteJob.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteJob.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.jobs = state.jobs.filter(job => job.id !== action.payload);
        state.total -= 1;
        state.totalPages = Math.ceil(state.total / state.pageSize);
        if (state.currentPage > state.totalPages && state.totalPages > 0) {
          state.currentPage = state.totalPages;
        }
      })
      .addCase(deleteJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(toggleJobStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(toggleJobStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        const index = state.jobs.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.jobs[index] = action.payload;
        }
      })
      .addCase(toggleJobStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default jobSlice.reducer;